import { capitalize } from '@mui/material';
import { getEntityIcon } from './entities';
import { routes } from '../app/routes';
import { Route, NavGroups, NavItem } from '../types/navigation.types';
import { match } from 'path-to-regexp';

export const flattenRoutes = (routes: Route[]): Route[] => {
  const flatRoutes: Route[] = [];

  const traverseRoutes = (routes: Route[]) => {
    routes.forEach((route) => {
      flatRoutes.push(route);
      if (route.children) {
        traverseRoutes(route.children);
      }
    });
  };

  traverseRoutes(routes);
  return flatRoutes;
};

export const getFlatRoutes = (routes: Route[]): Route[] =>
  flattenRoutes(routes).map(({ element, children, ...rest }) => ({
    ...rest,
  }));

export const getNavGroups = (routes: Route[]): NavGroups =>
  getFlatRoutes(routes).reduce((acc: NavGroups, route) => {
    const { navGroup, entityTypes } = route;
    if (navGroup) {
      if (!acc[navGroup]) {
        acc[navGroup] = { entityTypes: [], navItems: [] };
      }

      acc[navGroup].entityTypes = [
        ...new Set([...acc[navGroup].entityTypes, ...(entityTypes || [])]),
      ];

      acc[navGroup].navItems = acc[navGroup].entityTypes.map((entityType) => {
        return {
          title: capitalize(entityType),
          icon: getEntityIcon(entityType),
          name: entityType,
          path: entityType,
        };
      });
    }
    return acc;
  }, {});

export const getBreadcrumbNavItems = (routes: Route[]) => () =>
  Object.values(getNavGroups(routes)).reduce((acc: NavItem[], { navItems }) => {
    return [...acc, ...navItems];
  }, []);

/**
 * Checks if a given path matches any route pattern in the app.
 * @param path The path to check (e.g. /accountId/flows/123)
 * @returns true if the path matches a route pattern, false otherwise
 */
export function isValidRoutePath(path: string): boolean {
  // Remove trailing slash for consistency
  const normalizedPath = path.replace(/\/$/, '');
  const flatRoutes = getFlatRoutes(routes).filter((r): r is { path: string } =>
    Boolean(r.path),
  );
  for (const route of flatRoutes) {
    // Replace React Router params (:param) with path-to-regexp style
    const pattern = route.path.startsWith('/') ? route.path : `/${route.path}`;
    // Account for parent params (e.g. /:accountId/flows/:entityId)
    // We'll try to match the path against all possible parent/child combinations
    try {
      const matcher = match(pattern, { decode: decodeURIComponent });
      if (matcher(path)) {
        return true;
      }
    } catch (e) {
      // ignore invalid patterns
    }
  }
  return false;
}
